import lazyVideoHelper from "./lazyVideos-helper";

//---------------- Utils -------------------------------//

const debounce = (fn, wait = 150) => {
  let id;
  return (...args) => {
    clearTimeout(id);
    id = setTimeout(() => fn(...args), wait);
  };
};

//------------------------------------------------------//

const assetVideo = {
  props: { cssSelector: ".c-video" },

  //-----------------------------------------------------------------------//
  //----------------------------- 1. Init ---------------------------------//
  //-----------------------------------------------------------------------//

  init() {
    this.initVideos();
  },

  //-----------------------------------------------------------------------//
  //--------------------------- 2. initVideos -----------------------------//
  //-----------------------------------------------------------------------//

  initVideos(baseDom = document) {
    if (window.bcl?.editMode) {
      return;
    }

    const baseFind = baseDom.classList?.contains("video-generic") ? baseDom.parentNode : baseDom;

    // Load CSS only once
    if (!assetVideo._cssLoaded) {
      window.bcl?.u?.loadStyleSheet?.("/etc.clientlibs/barcelo/clientlibs/clientlib-assetVideo.css");
      assetVideo._cssLoaded = true;
    }

    // Process videos main function
    const processVideos = (root) => {
      const newVideos = root.querySelectorAll(".c-video--video-node:not([data-processed])");

      // Autoplay + muted
      root.querySelectorAll(".swiper-wrapper[data-items-load] .c-video--video-node:not([data-processed])").forEach((v) => {
        v.setAttribute("vdautoplay", "true");
        v.setAttribute("vdmuted", "true");
      });

      // Check if video is dynamic media
      newVideos.forEach((video) => {
        const dynamicMedia = video.getAttribute("videoDesktopPathDM") !== "DM-not-configured" || video.getAttribute("videoMobilePathDM") !== "DM-not-configured";

        video.setAttribute("dynamicMedia", dynamicMedia);
        ["vdcontrols", "vdmuted"].forEach((a) => video.setAttribute(a, video.hasAttribute(a)));
        video.setAttribute("data-processed", "true");

        this.setupBestSource(video);
      });

      // Lazy-load + resize watchers
      lazyVideoHelper.addLazyVideos(root);
      this.changeVideosSourceOnResize(newVideos);
    };

    processVideos(baseFind);

    // Observe DOM changes
    const mo = new MutationObserver((muts) => {
      muts.forEach((m) =>
        m.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && (node.querySelector(".c-video--video-node:not([data-processed])") || node.querySelector(".swiper-wrapper[data-items-load] .c-video--video-node:not([data-processed])"))) {
            processVideos(node);
          }
        }),
      );
    });
    mo.observe(baseFind, { childList: true, subtree: true });

    this.cleanupObserver = () => mo.disconnect();
  },

  //-----------------------------------------------------------------------//
  //------------------------ 3. setupBestSource ---------------------------//
  //-----------------------------------------------------------------------//

  setupBestSource(video) {
    const isMobile = window.bcl?.u?.mobile?.isMobile?.() ?? false;
    const dynamic = video.getAttribute("dynamicMedia") === "true";

    let srcDesktop, srcMobile;

    if (dynamic) {
      const appleHls = video.canPlayType("application/vnd.apple.mpegurl");
      video.setAttribute("canPlayNativeDM", appleHls);

      srcDesktop = video.getAttribute("videoDesktopPathDM") !== "DM-not-configured" ? video.getAttribute("videoDesktopPathDM") : undefined;
      srcMobile = video.getAttribute("videoMobilePathDM") !== "DM-not-configured" ? video.getAttribute("videoMobilePathDM") : undefined;
    } else {
      srcDesktop = video.getAttribute("videoDesktopPath") || undefined;
      srcMobile = video.getAttribute("videoMobilePath") || undefined;
    }

    const chosen = isMobile ? srcMobile || srcDesktop : srcDesktop || srcMobile;
    if (!chosen) {
      return;
    }

    video.setAttribute("lastDevice", isMobile ? "mobile" : "desktop");

    const sourceEl = video.querySelector(".videoDynamicPath");
    if (!sourceEl) {
      return;
    }

    sourceEl.src = chosen;
    sourceEl.type = dynamic ? "application/x-mpegURL" : "video/mp4";
  },

  //-----------------------------------------------------------------------//
  //------------------ 4. changeVideosSourceOnResize ----------------------//
  //-----------------------------------------------------------------------//

  changeVideosSourceOnResize(videos) {
    const onResize = () => {
      const isMobile = window.bcl?.u?.mobile?.isMobile?.() ?? false;

      videos.forEach((video) => {
        const last = video.getAttribute("lastDevice");
        if ((isMobile && last === "desktop") || (!isMobile && last === "mobile")) {
          this.setupBestSource(video);

          const dynamic = video.getAttribute("dynamicMedia") === "true";
          const native = video.getAttribute("canPlayNativeDM") === "true";

          if (dynamic && native) {
            video.load();
          } else {
            lazyVideoHelper.customPlayerLoadAndPlay(video);
          }
        }
      });
    };

    window.addEventListener("resize", debounce(onResize));
  },

  //-----------------------------------------------------------------------//
  //-------------------- 5. stopAllVideosWithSound ------------------------//
  //-----------------------------------------------------------------------//

  stopAllVideosWithSound(baseDom = document) {
    baseDom.querySelectorAll(".video-generic").forEach((v) => {
      if (v.getAttribute("vdautoplay") === "true" && v.getAttribute("vdmuted") && !v.getAttribute("vdcontrols")) {
        return;
      }
      v.pause?.();
    });

    // YouTube
    baseDom.querySelectorAll(".video-youtube").forEach((el) => el.contentWindow?.postMessage?.('{"event":"command","func":"pauseVideo","args":""}', "*"));

    // Vimeo
    baseDom.querySelectorAll(".video-vimeo").forEach((el) => el.contentWindow?.postMessage?.('{"method":"pause"}', "*"));
  },
};

export default assetVideo;
